'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';

export default function Custom500ServerError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const lines = React.useMemo(() => ['500', 'SERVER', 'ERROR'], []);

  // Variants for glitching letters with continuous animation
  const letterVariants = {
    initial: {
      y: 0,
      x: 0,
      opacity: 1,
      scale: 1,
      rotateX: 0,
    },
    animate: {
      y: [-20, 20, -15, 15, 0],
      x: [15, -15, -10, 10, 0],
      opacity: [1, 0.7, 0.9, 1, 0.8],
      scale: [1, 1.1, 0.9, 1, 1.05],
      rotateX: [0, 10, -10, 0, 5],
      transition: {
        duration: 4,
        ease: 'easeInOut',
        repeat: Infinity,
      },
    },
  };

  const backgroundVariants = {
    initial: {
      opacity: 0,
      scale: 1.1,
    },
    animate: {
      opacity: [0.7, 1, 0.9, 0.8, 1],
      scale: [1.1, 1, 1.05, 0.95, 1],
      transition: {
        duration: 6,
        ease: 'easeInOut',
        repeat: Infinity,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 30,
    },
    animate: {
      opacity: 1,
      y: [30, 0, 5, 0],
      transition: {
        duration: 2,
        ease: 'easeOut',
        repeat: Infinity,
      },
    },
  };

  console.error('500 Error:', error);

  return (
    <div className="relative w-screen h-screen bg-gray-900 overflow-hidden flex items-center justify-center text-white">
      {/* Main glitch animation layer */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
        {lines.map((text, lineIndex) => (
          <div
            key={`line-${lineIndex}`}
            className="flex justify-center items-center min-h-[25vh]"
          >
            {text.split('').map((char, charIndex) => (
              <motion.div
                key={`char-${lineIndex}-${charIndex}`}
                className="font-mono text-[15vw] md:text-[20vw] font-extrabold leading-none select-none"
                variants={letterVariants}
                initial="initial"
                animate="animate"
                style={{
                  textShadow: '0 0 8px rgba(255,255,255,0.5)',
                }}
              >
                {char}
              </motion.div>
            ))}
          </div>
        ))}
      </div>

      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0 z-10"
        variants={backgroundVariants}
        initial="initial"
        animate="animate"
        aria-hidden="true"
      >
        <div className="w-full h-full bg-gradient-to-br from-red-900 via-purple-900 to-red-700">
          <div className="absolute inset-0 bg-[radial-gradient(circle,transparent_1%,rgba(0,0,0,0.3)_100%)] opacity-50" />
        </div>
      </motion.div>

      {/* Error content */}
      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-center z-30"
        variants={contentVariants}
        initial="initial"
        animate="animate"
        role="alert"
        aria-label="500 error message"
      >
        <div className="text-center space-y-8">
          <h1 className="text-3xl md:text-5xl font-bold">
            Oops! Something went wrong on our end
          </h1>
          <p className="text-lg md:text-xl text-red-200">
            We are working to fix this. Please try again in a few moments.
          </p>
          <div className="space-x-4">
            <button
              onClick={() => reset()}
              className="inline-block px-8 py-4 bg-red-600 text-white text-lg font-bold rounded-lg
                       hover:bg-red-700 transform hover:scale-105 transition-all duration-200
                       focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-gray-900"
              role="button"
              aria-label="Retry current page"
            >
              Try Again
            </button>
            <Link
              href="/"
              className="inline-block px-8 py-4 bg-white text-red-900 text-lg font-bold rounded-lg
                       hover:bg-red-100 transform hover:scale-105 transition-all duration-200
                       focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-red-900"
              role="button"
              aria-label="Return to home page"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}